<script>
	// NPM Modules
	import dayjs from 'dayjs';
	// Stores
	import LinkGroupStore from '@/Stores/LinkGroupStore.js';
	// Components
	import Datepicker from '@/Components/Datepicker.svelte';
	import TextInput from '@/Components/TextInput.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	import CheckBox from '@/Components/CheckBox.svelte';
	import Prompt from '@/Components/Prompt.svelte';
	import Icon from '@/Components/Icon.svelte';
	import Switch from '@/Components/Switch.svelte';
	import PillMultiSelect from '@/Components/PillMultiSelect.svelte';
	// Partials
	import LinkImage from '@/Partials/LinkGroup/_LinkImage.svelte';
	// Services
	import { notify } from '@/Services/Notify.js';
	// Constants
	import { UserRating, UserRatingName } from '@/Constants/User.js';
	import { LinkGroupCategories } from '@/Constants/LinkGroups.js';

	export let link;
	export let linkGroup;
	export let linkGroupTypeId;
	export let linkCategoryId;
	export let first;
	export let last;

	let newImages = { desktop: null, mobile: null };
	let sending = false;
	let deleting = false;
	let promptDelete = false;

	let activeDate = dayjs(link.active_from ?? undefined).format('YYYY-MM-DD');
	let activeTime = dayjs(link.active_from ?? undefined).format('HH:mm');
	let expireDate = dayjs(link.active_to ?? undefined).format('YYYY-MM-DD');
	let expireTime = dayjs(link.active_to ?? undefined).format('HH:mm');

	let activeToggle = !!link.active_from;
	let expireToggle = !!link.active_to;

	$: activeStart = dayjs(activeDate + ' ' + activeTime + ':00');
	$: expireStart = dayjs(expireDate + ' ' + expireTime + ':00');
	$: activeStart && expireStart && updateDateTimes();

	$: desktopImageURL = link && link.images.desktop + '?time=' + Math.round(Date.now() / 1000);
	$: mobileImageURL = link && link.images.mobile + '?time=' + Math.round(Date.now() / 1000);

	function updateDateTimes() {
		if (activeToggle) {
			link.active_from = dayjs(activeStart).format('YYYY-MM-DDTHH:mm:00Z');
		} else {
			link.active_from = null;
		}
		if (expireToggle) {
			link.active_to = dayjs(expireStart).format('YYYY-MM-DDTHH:mm:00Z');
		} else {
			link.active_to = null;
		}
	}

	function onSave() {
		// this covers all scenarios of updating/deleting images
		let postImages = {};
		if (newImages.desktop) postImages.desktop = newImages.desktop;
		if (newImages.mobile) postImages.mobile = newImages.mobile;
		if (link.images.desktop === null && !newImages.desktop) postImages.desktop = null;
		if (link.images.mobile === null && !newImages.mobile) postImages.mobile = null;

		link.images = postImages;

		sending = true;
		if (link.is_public) {
			link.user_rating_ids = null;
		}
		LinkGroupStore.updateLink(link, linkGroupTypeId, linkCategoryId)
			.then(() => {
				notify({
					type: 'success',
					title: 'Link',
					message: 'Link updated',
					timeout: 3
				});
				newImages = { desktop: null, mobile: null };
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Failed to update Link',
					timeout: 5
				});
			})
			.finally(() => (sending = false));
	}

	function onDelete() {
		deleting = true;
		LinkGroupStore.deleteLink(link, linkGroup.id)
			.then(() => {
				notify({
					type: 'success',
					title: 'Link',
					message: 'Link Deleted',
					timeout: 3
				});
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Failed to delete the Link',
					timeout: 5
				});
			})
			.finally(() => (deleting = false));
	}

	function onMove(newLinkCategoryId) {
		sending = true;
		LinkGroupStore.moveLink(link, linkGroupTypeId, linkCategoryId, newLinkCategoryId)
			.then(() => {
				notify({
					type: 'success',
					title: 'Link',
					message: 'Link Moved',
					timeout: 3
				});
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Failed to move the Link',
					timeout: 5
				});
			})
			.finally(() => (sending = false));
	}

	const UserRatings = () => {
		const ratings = {};
		for (const rating of Object.keys(UserRating)) {
			if (
				![
					UserRating.BONUS_ABUSER,
					UserRating.FEMALE,
					UserRating.TIPPING,
					UserRating.TOUGH_SPORT,
					UserRating.TOUGH
				].includes(UserRating[rating])
			) {
				// eslint-disable-next-line quotes
				ratings[UserRatingName[UserRating[rating]]] = UserRating[rating];
			}
		}

		return ratings;
	};

	function shiftOrderUp() {
		LinkGroupStore.changeLinkOrder(linkGroup.id, link.order, link.order - 1);
	}

	function shiftOrderDown() {
		LinkGroupStore.changeLinkOrder(linkGroup.id, link.order, link.order + 1);
	}

	function updateFilter(e) {
		link.user_rating_ids = e.detail.checked;
		if (Array.isArray(link.user_rating_ids) && link.user_rating_ids.length == 0) {
			link.user_rating_ids = null;
		}
	}
	function changeLinkCategory(e) {
		onMove(e.detail.checked[0]);
	}
</script>

<Prompt
	bind:open={promptDelete}
	title="Confirm delete"
	message="This can't be undone. Are you sure?"
	onConfirm={() => onDelete()}
/>

<div class="p-2 bg-white border">
	<div class="flex flex-row">
		<div class="flex-1">
			<div class="flex space-x-1">
				<div class="flex flex-col justify-center items-center">
					<div class="flex flex-col items-center justify-center">
						<button
							on:click={() => shiftOrderUp()}
							disabled={link.order === first}
							class:opacity-0={link.order === first}
						>
							<Icon name="up-arrow" classes="w-7 h-7" />
						</button>
						<span class="my-px">{link.order}</span>
						<button
							on:click={() => shiftOrderDown()}
							disabled={link.order === last}
							class:opacity-0={link.order === last}
						>
							<Icon name="down-arrow" classes="w-7 h-7" />
						</button>
					</div>
				</div>
				<div class="flex flex-col justify-center items-center grabber cursor-move">
					<Icon name="drag-drop" classes="w-4 h-4" />
				</div>
				<div class="flex flex-col">
					<TextInput bind:value={link.name} label="Title" name={`${link.id}-name`} />
					<TextInput bind:value={link.url} label="URL" name={`${link.id}-url`} />
				</div>
				<div class="flex space-x-4 text-center">
					<div class="flex flex-col justify-center items-center">
						<div class="form-label">Desktop</div>
						<LinkImage bind:image={link.images.desktop} bind:newImage={newImages.desktop} imageUrl={desktopImageURL} />
					</div>
					<div class="flex flex-col justify-center items-center">
						<div class="form-label">Mobile</div>
						<LinkImage bind:image={link.images.mobile} bind:newImage={newImages.mobile} imageUrl={mobileImageURL} />
					</div>
				</div>
				<div class="flex flex-grow">
					<div class="flex flex-col flex-grow">
						<div class="flex flex-grow items-center">
							<div class="w-1/2 flex flex-col justify-center items-center space-y-1">
								<div class="flex flex-row items-center">
									<label class="form-label mb-0.5" for={`${link.id}-activeTime`}>Publish at:</label>
									<Switch name={`${link.id}-activeToggle`} bind:checked={activeToggle} small={true} className="ml-1" />
								</div>
								<div class="flex flex-col items-center justify-center space-y-1">
									{#if activeToggle}
										<Datepicker
											selected={activeDate}
											dateChosen={(date) => {
												activeDate = dayjs(date).format('YYYY-MM-DD');
											}}
											displayFormat="DD-MM-YYYY"
											appendBtnStyle="h-10 w-30"
										/>
										<TextInput
											bind:value={activeTime}
											inputClasses="h-10 w-30"
											name={`${link.id ?? link.temp_id}-activeTime`}
											type="time"
										/>
									{/if}
								</div>
							</div>
							<div class="w-1/2 flex flex-col items-center justify-center space-y-1">
								<div class="flex flex-row items-center">
									<label class="form-label mb-0.5" for={`${link.id}-expireTime`}>Expires:</label>
									<Switch name={`${link.id}-expireToggle`} bind:checked={expireToggle} small={true} className="ml-1" />
								</div>
								<div class="flex flex-col items-center justify-center space-y-1">
									{#if expireToggle}
										<Datepicker
											selected={expireDate}
											displayFormat="DD-MM-YYYY"
											dateChosen={(date) => {
												expireDate = dayjs(date).format('YYYY-MM-DD');
											}}
											appendBtnStyle="h-10 w-30"
										/>
										<TextInput
											bind:value={expireTime}
											inputClasses="h-10 w-30"
											name={`${link.id ?? link.temp_id}-expireTime`}
											type="time"
										/>
									{/if}
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="flex flex-col justify-center items-center">
					<div class="flex flex-row justify-around w-full">
						<div class="flex flex-col items-center">
							<div class="form-label">Active</div>
							<div class="flex flex-1">
								<CheckBox bind:checked={link.active} center={true} />
							</div>
						</div>
						<div class="flex flex-col items-center">
							<div class="form-label">Public</div>
							<div class="flex flex-1">
								<CheckBox bind:checked={link.is_public} center={true} />
							</div>
						</div>
					</div>
					<div class="flex flex-row pt-1 border-t border-gray-300 mt-2">
						<PillMultiSelect
							name="rating"
							unselectedText="Select ratings"
							className="w-40"
							altLabel={true}
							disabled={link.is_public}
							singleSelection={false}
							on:change={(e) => updateFilter(e)}
							checked={link.user_rating_ids ?? []}
							options={Object.entries(UserRatings()).map(([key, value]) => [value, key])}
							action=""
						/>
					</div>
				</div>
			</div>
		</div>
		<div class="flex flex-col justify-center ml-4 space-y-2">
			<LoadingButton on:click={onSave} className="btn btn-primary" loading={sending}>
				{link.temp_id ? 'Create' : 'Save'}
			</LoadingButton>
			<LoadingButton
				on:click={() => (link.new ? onDelete() : (promptDelete = true))}
				className="btn btn-primary-action"
				loading={deleting}
				>Delete
			</LoadingButton>
			<div class="flex flex-row border-t border-gray-300">
				<PillMultiSelect
					name="move"
					unselectedText="Move to"
					singleSelection={true}
					on:change={(e) => changeLinkCategory(e)}
					checked={[link.link_category.id]}
					options={Object.entries(LinkGroupCategories).map(([key, value]) => [value, key])}
					action=""
				/>
			</div>
		</div>
	</div>
</div>
